import {browserDeviceSelected} from '../shared/browser-navigation.js';
import type {HealthMetric,HealthSummary} from './health-summary-types.ts';
import type {BatteryAlertInput} from './battery-alert-types.ts';
import type {Appearance,StoredAppearance} from '../shared/appearance-types';
import {hourlyActivity} from '../shared/period-charts.js';
import {activityDayBounds} from '../shared/activity-model.js';
import {previousActivityAnchor} from "../shared/fitness-model.js";
import type {SessionVault} from "./session-vault";
import {DEFAULT_DASHBOARD,validDashboard,validUploadInterval,validTelemetryWindow} from "../shared/product-settings.js";
import type {DashboardLayout} from '../shared/dashboard-layout-types';
import { hardwareBaseUrl, localBaseUrl, LocalApiClient, LocalApiError, type LocalRouteHistory, type LocalSnapshot, type TelemetryWindow, type TestDataMode } from "./local-api.ts";
import {POSITION_REQUEST_LOCK_MS,newGpsObservation} from "./position-status.ts";
import { followPositionRequest, type PositionProgress } from "./position-followup.ts";
import { followFindRequest, type FindProgress } from "./find-followup.ts";
import { followUploadRequest, type UploadProgress } from "./upload-followup.ts";
import type { ActivityPeriod, ActivitySummary } from "./activity-types.ts";
import type { HardwareCommandType } from "./hardware-types.ts";
import type { ArchiveInput, ArchivePage } from "./archive-types.ts";
import { activeMeasurement } from "../shared/hardware-measurement.js";

export type SessionNotice = { key: string; error: boolean; loginEmail?:string; attemptsRemaining?:number; retryUntil?:number; attemptsResetUntil?:number } | null;
export type LocalSessionState = {
  restoring?:boolean; networkUnavailable?:boolean; dataUnavailable?:boolean; cachedSnapshot?:boolean;
  passwordBusy?: boolean; passwordError?: string | null;
  layoutPreference?: "simple" | "modern" | "technical" | "modern2" | "accessible" | null; layoutBusy?: boolean;
  appearancePreference?:StoredAppearance|null;
  activitySummary?: ActivitySummary | null; previousActivitySummary?: ActivitySummary | null; activityComparisonError?: boolean; activityComparisonBusy?: boolean; activityBusy?: boolean; activityError?: string | null;
  profileBusy?: boolean; profileError?: string | null;
  address: string; signedIn: boolean; busy: boolean;
  accountEmail: string | null;
  mode: TestDataMode;
  positionProgress: PositionProgress | null;
  positionCooldownUntil?:number; positionRequestDeviceId?:string;
  findProgress: FindProgress | null;
  uploadProgress: UploadProgress | null;
  telemetryMinutes: TelemetryWindow;
  dashboard?: string[]; dashboardLayout?:DashboardLayout; dashboardBusy?: boolean; dashboardError?:string|null;
  routeHistory: LocalRouteHistory | null;
  routeBusy: boolean;
  routeRangeKey: string | null;
  routeError?: string | null;
  routeLoadedAt?: string | null;
  autoRefreshing: boolean;
  snapshot: LocalSnapshot | null; notice: SessionNotice;
};

// Owns one session across tabs. Each completion belongs to its originating operation.
export class LocalTrackerSession {
  private photoCache:{deviceId:string;dataUri:string|null;revision:number}|null=null;
  private state: LocalSessionState;
  private client: LocalApiClient | null = null;
  private selectedDeviceId: string | undefined;
  private revision = 0;
  private backgroundRevision = 0;
  private routeRevision = 0;
  private activityRevision = 0;
  private profileRevision = 0;
  private listeners = new Set<() => void>();
  private expiryTimer: ReturnType<typeof setTimeout> | null = null;
  private refreshTimer: ReturnType<typeof setTimeout> | null = null;
  private foreground = false;
  private positionAttempts=new Map<string,number>();
  private positionObservation: { deviceId: string; baselineId: number; requestedAt: number } | null = null;
  private following: "position" | "find" | "upload" | null = null;
  private refreshIntervalMs: number;
  private positionWait: { timer: ReturnType<typeof setTimeout>; resolve: () => void } | null = null;
  private vault?:SessionVault;
  private revocationRun:Promise<void>|null=null;
  private needsRevocation=false;
  private makeClient: (address: string, mode: TestDataMode) => LocalApiClient;

  constructor(address = "http://127.0.0.1:8787", makeClient = (value: string, mode: TestDataMode) => new LocalApiClient(value, globalThis.fetch, 8000, mode), refreshIntervalMs = 15000, vault?:SessionVault) {
    this.state = { address, mode: "simulator", signedIn: false, busy: false, accountEmail: null, snapshot: null, notice: null, positionProgress: null, findProgress: null, uploadProgress: null, telemetryMinutes: 60, routeHistory: null, routeBusy: false, routeRangeKey: null, routeError: null, routeLoadedAt: null, autoRefreshing: false };
    this.vault = vault;
    this.refreshIntervalMs = refreshIntervalMs;
    this.makeClient = makeClient;
  }
  private vaultWrites:Promise<void>=Promise.resolve();
  private writeVault(value:import("./session-vault").RememberedLogin|null){
    if(!this.vault)return Promise.resolve();const vault=this.vault;
    const next=this.vaultWrites.catch(()=>{}).then(()=>value?vault.save(value):vault.remove());this.vaultWrites=next;return next;
  }
  private cacheSnapshot(snapshot:LocalSnapshot){
    const owner=this.client?.rememberedLogin,vault=this.vault;
    if(!owner||!vault?.saveSnapshot)return Promise.resolve();
    const scoped={...owner,selectedDeviceId:this.selectedDeviceId};
    const next=this.vaultWrites.catch(()=>{}).then(()=>vault.saveSnapshot!(scoped,snapshot));
    this.vaultWrites=next;return next.catch(()=>{});
  }
  private clearCachedSnapshot(){const vault=this.vault;if(!vault?.clearSnapshot)return Promise.resolve();const next=this.vaultWrites.catch(()=>{}).then(()=>vault.clearSnapshot!());this.vaultWrites=next;return next.catch(()=>{});}
  private async persist(){const value=this.client?.rememberedLogin;await this.writeVault(value?{...value,selectedDeviceId:this.selectedDeviceId}:null);if(value?.installationId)await this.vault?.saveInstallation?.(value.address,value.email,value.installationId).catch(()=>{});}
  drainRevocations = async ():Promise<void> => {
    const vault=this.vault;if(!vault?.pendingRevocations||!vault.removeRevocation)return;
    if(this.revocationRun)return this.revocationRun;
    const run=(async()=>{try{
      const pending=await vault.pendingRevocations!();this.needsRevocation=pending.length>0;
      // Bound each pass so a failed connection cannot delay opening the app.
      const entry=pending[0];if(!entry)return;
      const client=this.makeClient(entry.address,entry.mode);client.restore(entry);
      await client.logout();await vault.removeRevocation!(entry.refreshToken);
      this.needsRevocation=(await vault.pendingRevocations!()).length>0;
    }catch{this.needsRevocation=true;/* Retry a failed protected-storage read as well as a failed network call. */}})();
    this.revocationRun=run;try{await run;}finally{if(this.revocationRun===run)this.revocationRun=null;this.scheduleRefresh();}
  };
  async restore(requiredService?:{address:string;mode:TestDataMode}){
    void this.drainRevocations();
    if(!this.vault)return;const revision=++this.revision;this.publish({restoring:true,busy:true});
    try{let stored;try{stored=await this.vault.load();}catch{throw new LocalApiError("secureStorage");}if(revision!==this.revision)return;if(!stored){this.publish({restoring:false,busy:false});return;}
      if(requiredService){
        const normalize=requiredService.mode==="hardware-test"?hardwareBaseUrl:localBaseUrl;
        const address=normalize(requiredService.address);
        if(stored.mode!==requiredService.mode||normalize(stored.address)!==address){
          // A server migration requires a fresh login. Never redirect a saved
          // refresh credential or display another server's cached tracker data.
          this.client?.clear();this.client=null;this.cancelExpiry();this.selectedDeviceId=undefined;
          try{
            await this.vaultWrites.catch(()=>{});
            if(this.vault.queueRevocation){await this.vault.queueRevocation(stored);this.needsRevocation=true;}
            else await this.writeVault(null);
          }catch{throw new LocalApiError("secureStorage");}
          if(revision!==this.revision)return;
          await this.clearCachedSnapshot();if(revision!==this.revision)return;
          this.publish({address,mode:requiredService.mode,signedIn:false,accountEmail:null,snapshot:null,routeHistory:null,networkUnavailable:false,restoring:false,busy:false,notice:{key:"local.serverChanged",error:false}});
          void this.drainRevocations();return;
        }
      }
      const client=this.makeClient(stored.address,stored.mode);this.client=client;client.restore(stored);this.selectedDeviceId=stored.selectedDeviceId;
      this.publish({address:client.baseUrl,mode:stored.mode,accountEmail:stored.email,signedIn:true,networkUnavailable:true});
      const cached=await this.vault.loadSnapshot?.(stored).catch(()=>null);if(revision!==this.revision)return;
      if(cached)this.publish({snapshot:cached,cachedSnapshot:true,networkUnavailable:true});
      await client.resume();if(revision!==this.revision)return;
      this.publish({layoutPreference:client.appearance?.layout??null,appearancePreference:client.appearance,dashboard:client.dashboard,dashboardLayout:client.dashboardLayout});
      const snapshot=await client.snapshot(this.selectedDeviceId,this.state.telemetryMinutes);if(revision!==this.revision)return;
      this.selectedDeviceId=snapshot.device?.id;await this.persist();if(revision!==this.revision)return;
      this.publish({snapshot,busy:false,restoring:false,cachedSnapshot:false});await this.cacheSnapshot(this.state.snapshot??snapshot);if(revision===this.revision)this.armExpiry();
    }catch(error){if(revision===this.revision){this.fail(error,error instanceof LocalApiError&&error.code==="secureStorage");this.publish({restoring:false});}}
  }
  getSnapshot = () => this.state;
  get selectedTrackerId() { return this.selectedDeviceId; }
  subscribe = (listener: () => void) => { this.listeners.add(listener); return () => { this.listeners.delete(listener); }; };
  private publish(update: Partial<LocalSessionState>) {
    // A foreground operation supersedes a background read without disabling
    // every control while the regular 15-second refresh is in progress.
    if(update.busy===true){this.backgroundRevision++;update={...update,autoRefreshing:false};}
    if(update.signedIn===false || update.snapshot===null || update.positionProgress===null || ['gps','cancelled','failed'].includes(update.positionProgress??'')) this.positionObservation=null;
    const observation=this.positionObservation,progress=update.positionProgress??this.state.positionProgress;
    // A fix may arrive after the one-minute foreground wait. Reconcile the
    // result on ordinary refreshes without sending another tracker command.
    if(observation && update.snapshot?.device?.id===observation.deviceId && update.networkUnavailable!==true && update.cachedSnapshot!==true && ['noFix','noReport'].includes(progress??'')) {
      const now=Date.now();
      if([update.snapshot.position,update.snapshot.lastValidPosition].some(position=>newGpsObservation(position??null,observation.baselineId,observation.requestedAt,now))) {
        update={...update,positionProgress:'gps'};
        this.positionObservation=null;
      }
    }
    // A profile shell is an intentional incomplete snapshot. Only a completed
    // response may clear that flag; otherwise empty events look like a real zero.
    if(update.snapshot&&update.networkUnavailable===undefined)update={...update,networkUnavailable:false,dataUnavailable:update.dataUnavailable??false,cachedSnapshot:false};
    if (update.signedIn === false) update = { ...update, networkUnavailable:false,dataUnavailable:false,cachedSnapshot:false, layoutPreference: null, appearancePreference:null, layoutBusy: false, passwordBusy: false, passwordError: null, dashboard: [...DEFAULT_DASHBOARD], dashboardLayout: undefined, dashboardBusy: false };
    if (update.signedIn === false || (Object.hasOwn(update, "snapshot") && update.snapshot === null)) {
      this.photoCache=null;this.activityRevision++; this.profileRevision++;
      update = { ...update, activitySummary: null, previousActivitySummary: null, activityComparisonError: false, activityBusy: false, activityError: null, profileBusy: false, profileError: null };
    }
    // A refresh started before a profile save must not put an older profile back on screen.
    const currentProfile = this.state.snapshot?.device?.activityProfile;
    if (currentProfile && update.snapshot?.device?.id === currentProfile.deviceId
      && (update.snapshot.device.activityProfile?.revision ?? 0) < currentProfile.revision) {
      update = { ...update, snapshot: { ...update.snapshot, device: { ...update.snapshot.device, activityProfile: currentProfile } } };
    }
    const currentZone = this.state.snapshot?.device?.gpsZone;
    const currentZoneRevision=this.state.snapshot?.device?.gpsZoneRevision??currentZone?.revision??0;
    if (currentZoneRevision && update.snapshot?.device && update.snapshot.device.id === this.state.snapshot?.device?.id
      && (update.snapshot.device.gpsZoneRevision??update.snapshot.device.gpsZone?.revision??0) < currentZoneRevision) {
      update = { ...update, snapshot: { ...update.snapshot, device: { ...update.snapshot.device, gpsZone: currentZone??null,gpsZoneRevision:currentZoneRevision } } };
    }
    const zoneSet=this.state.snapshot?.device?.gpsZones;
    if(zoneSet&&update.snapshot?.device&&update.snapshot.device.id===this.state.snapshot?.device?.id&&(update.snapshot.device.gpsZones?.revision??0)<zoneSet.revision){
      update={...update,snapshot:{...update.snapshot,device:{...update.snapshot.device,gpsZones:zoneSet}}};
    }
    this.state = { ...this.state, ...update };
    for (const listener of this.listeners) listener();
    this.scheduleRefresh();
  }
  private scheduleRefresh() {
    if (this.refreshTimer) clearTimeout(this.refreshTimer);
    this.refreshTimer = null;
    const hardware = this.state.snapshot?.hardware;
    const measuring = hardware && activeMeasurement(hardware.commands, hardware.reports);
    if (this.foreground && (this.state.signedIn||this.needsRevocation) && !this.state.busy && !this.state.autoRefreshing) this.refreshTimer = setTimeout(() => {
      this.refreshTimer = null;
      void this.drainRevocations();
      if(this.state.signedIn)void this.refresh(true);else this.scheduleRefresh();
    }, measuring ? Math.min(3000, this.refreshIntervalMs) : this.refreshIntervalMs);
  }
  setForeground = (active: boolean) => {
    if (active === this.foreground) return;
    this.foreground = active;
    if(active)void this.drainRevocations();
    this.checkExpiry();
    if (!active) {
      if (this.state.autoRefreshing) {
        this.revision += 1;
        this.client?.cancelPending();
        this.publish({ busy: false, autoRefreshing: false });
      }
      this.cancelPositionRequest();
      this.cancelFindRequest();
      this.cancelUploadRequest();
      if (this.state.routeBusy) {
        this.routeRevision += 1;
        this.client?.cancelPending();
        this.publish({ routeBusy: false });
      }
    }
    this.scheduleRefresh();
    if (active && this.state.signedIn && !this.state.busy) void this.refresh(true);
  };
  private cancelExpiry() {
    this.following = null;
    this.clearPositionWait();
    if (this.expiryTimer) clearTimeout(this.expiryTimer);
    this.expiryTimer = null;
  }
  private armExpiry() {
    if (this.expiryTimer) clearTimeout(this.expiryTimer);
    this.expiryTimer = null;
    if (this.client?.signedIn) this.expiryTimer = setTimeout(() => this.checkExpiry(), Math.max(1, this.client.expiry - Date.now()));
  }
  checkExpiry = () => {
    if (this.state.signedIn && !this.client?.signedIn) {
      this.revision += 1; this.client?.clear(); this.cancelExpiry();
      this.routeRevision += 1;
      this.publish({ signedIn: false, busy: false, accountEmail: null, autoRefreshing: false, snapshot: null, routeHistory: null, routeBusy: false, routeRangeKey: null, routeError: null, routeLoadedAt: null, positionProgress: null, findProgress: null, uploadProgress: null, notice: { key: "local.error.session", error: true } });
    } else if (this.state.signedIn) {
      // Timers can wake just before wall-clock expiry; keep the expiry check armed.
      this.armExpiry();
    }
  };
  async login(address: string, email: string, password: string, mode: TestDataMode = "simulator") {
    return this.startLogin(address,email,mode,(active,installationId)=>active.login(email,password,Boolean(this.vault),installationId));
  }
  async completeAccountLink(action:'verify-login'|'register-invited',body:Record<string,unknown>,address=this.state.address,mode=this.state.mode){
    return this.startLogin(address,typeof body.email==='string'?body.email:'',mode,active=>active.loginFromAccountLink(action,body,Boolean(this.vault)));
  }
  private async startLogin(address:string,email:string,mode:TestDataMode,authenticate:(client:LocalApiClient,installationId?:string)=>Promise<void>){
    if (this.state.busy || this.state.signedIn) return false;
    const revision = ++this.revision;
    this.client?.clear(); this.cancelExpiry();
    this.selectedDeviceId = undefined;
    this.routeRevision += 1;
    this.publish({ address: address.trim(), mode, busy: true, signedIn: false, accountEmail: null, snapshot: null, notice: null, positionProgress: null, findProgress: null, uploadProgress: null, routeHistory: null, routeBusy: false, routeRangeKey: null, routeError: null, routeLoadedAt: null, autoRefreshing: false });
    try {
      const active = this.makeClient(address, mode);
      this.client = active;
      const installationId=await this.vault?.loadInstallation?.(active.baseUrl,email).catch(()=>undefined);
      if(revision!==this.revision)return false;
      await authenticate(active,installationId);
      if(revision!==this.revision)return false;
      if(this.vault){try{await this.persist();}catch{await active.logout().catch(()=>{});throw new LocalApiError("secureStorage");}}
      if (revision !== this.revision) return false;
      this.armExpiry();
      this.publish({ address: active.baseUrl, signedIn: true, accountEmail: active.accountEmail ?? email.trim().toLowerCase(), dashboard: active.dashboard ?? [...DEFAULT_DASHBOARD], dashboardLayout:active.dashboardLayout, layoutPreference: active.appearance?.layout ?? null,appearancePreference:active.appearance });
      const snapshot = await active.snapshot(undefined, this.state.telemetryMinutes);
      if (revision !== this.revision) return false;
      this.selectedDeviceId = snapshot.device?.id;
      if(this.vault)await this.persist();
      if(revision!==this.revision)return false;
      this.publish({ snapshot, busy: false });
      await this.cacheSnapshot(this.state.snapshot??snapshot);
      return this.state.signedIn&&this.selectedDeviceId===snapshot.device?.id&&active===this.client;
    } catch (error) {
      if (revision === this.revision) {
        this.fail(error);
        if (error instanceof LocalApiError && !this.client?.signedIn && this.state.notice) this.publish({notice:{...this.state.notice,loginEmail:email.trim().toLowerCase(),attemptsRemaining:error.attemptsRemaining,
          retryUntil:error.retryAfterSeconds ? Date.now() + Math.min(60,error.retryAfterSeconds)*1000 : undefined,
          attemptsResetUntil:error.attemptsResetAfterSeconds ? Date.now()+error.attemptsResetAfterSeconds*1000 : undefined}});
      }
      return false;
    }
  }
  private fail(error: unknown, preserveVault=false) {
    this.following = null;
    const signedIn = Boolean(this.client?.signedIn);
    if (!signedIn){this.cancelExpiry();if(!preserveVault){void this.writeVault(null).catch(()=>{});void this.clearCachedSnapshot();}}
    this.clearPositionWait();
    this.routeRevision += 1;
    const accessLost=error instanceof LocalApiError&&['deviceMissing','devicePermission'].includes(error.code);
    if(accessLost){this.photoCache=null;this.activityRevision++;this.profileRevision++;void this.clearCachedSnapshot();this.publish({cachedSnapshot:false,activitySummary:null,previousActivitySummary:null,activityBusy:false,activityComparisonBusy:false,profileBusy:false});}
    const network=error instanceof LocalApiError&&['connection','server'].includes(error.code);
    const keep=Boolean(signedIn&&!accessLost&&this.state.snapshot&&this.state.snapshot.device?.id===this.selectedDeviceId);
    const devices=error instanceof LocalApiError?error.devices:undefined;
    if(signedIn&&!accessLost&&!this.selectedDeviceId&&devices?.length)this.selectedDeviceId=devices[0].id;
    const shell=devices?{devices,device:accessLost?null:devices.find(d=>d.id===this.selectedDeviceId)??null,position:null,events:[],loadedAt:new Date().toISOString()}:null;
    this.publish({ signedIn, busy: false, autoRefreshing: false, snapshot:signedIn?(keep?this.state.snapshot:shell):null, networkUnavailable:signedIn&&network,dataUnavailable:signedIn&&(!network||!keep||this.state.dataUnavailable===true), routeHistory:keep?this.state.routeHistory:null, routeBusy:false, routeRangeKey:keep?this.state.routeRangeKey:null, routeError:keep?`local.error.${network?'connection':'response'}`:null, routeLoadedAt:keep?this.state.routeLoadedAt:null, positionProgress: null, findProgress: null, uploadProgress: null, notice: {
      key: `local.error.${error instanceof LocalApiError ? error.code : "connection"}`, error: true,
    } });
  }
  private async run(action: (client: LocalApiClient, selectedId?: string) => Promise<LocalSnapshot>, key: string, clearSnapshot = false, automatic = false) {
    this.checkExpiry();
    if (this.state.busy || automatic&&this.state.autoRefreshing || !this.state.signedIn || !this.client) return false;
    const active = this.client;
    const selectedId = this.selectedDeviceId;
    const revision = ++this.revision;
    const backgroundRevision=this.backgroundRevision;
    const current=()=>revision===this.revision&&(!automatic||backgroundRevision===this.backgroundRevision);
    this.publish({ busy: !automatic, autoRefreshing: automatic, ...(automatic ? {} : { notice: null, positionProgress: null, uploadProgress: null }), ...(clearSnapshot ? { snapshot: null, findProgress: null } : {}) });
    try {
      const snapshot = await action(active, selectedId);
      if (!current()) return false;
      this.selectedDeviceId = snapshot.device?.id;
      if(this.vault)await this.persist();
      if(!current())return false;
      this.publish({layoutPreference:active.appearance?.layout??this.state.layoutPreference,appearancePreference:active.appearance??this.state.appearancePreference,dashboard:active.dashboard??this.state.dashboard,dashboardLayout:active.dashboardLayout??this.state.dashboardLayout});
      this.publish({ snapshot, busy: false, autoRefreshing: false, notice: automatic ? null : { key, error: false } });
      await this.cacheSnapshot(this.state.snapshot??snapshot);
      return this.state.signedIn&&this.selectedDeviceId===snapshot.device?.id&&active===this.client;
    } catch (error) {
      if (current()) this.fail(error);
      return false;
    }
  }
  async dailyMeasurements(expectedId:string,input?:import('./daily-measurement-types').DailyMeasurementInput){
    this.checkExpiry();const client=this.client,id=this.selectedDeviceId;
    if(!client||!id||id!==expectedId||!this.state.signedIn)throw new LocalApiError('session');
    if(input&&this.state.snapshot?.device?.access?.canConfigure===false)throw new LocalApiError('device_permission_denied');
    try{const plan=await client.dailyMeasurements(id,input);if(client!==this.client||id!==this.selectedDeviceId||!this.state.signedIn)throw new LocalApiError('cancelled');return plan;}
    catch(error){if(client===this.client&&!client.signedIn)this.checkExpiry();throw error;}
  }
  private readSnapshot = (client: LocalApiClient, id?: string) => client.snapshot(id, this.state.telemetryMinutes);
  private async careRequest<T>(expectedId:string,write:boolean,action:(client:LocalApiClient,id:string)=>Promise<T>):Promise<T>{
    this.checkExpiry();const client=this.client,id=this.selectedDeviceId;
    if(!client||!id||id!==expectedId||!this.state.signedIn)throw new LocalApiError('session');
    if(write&&this.state.snapshot?.device?.access?.canConfigure===false)throw new LocalApiError('device_permission_denied');
    try{const result=await action(client,id);if(client!==this.client||id!==this.selectedDeviceId||!this.state.signedIn)throw new LocalApiError('cancelled');return result;}
    catch(error){if(client===this.client&&!client.signedIn)this.checkExpiry();throw error;}
  }
  timePolicy=(id:string,input?:import('./care-types').TimePolicyInput)=>this.careRequest(id,!!input,(c,d)=>c.timePolicy(d,input));
  pulseAlerts=(id:string,input?:import('./care-types').PulseRule)=>this.careRequest(id,!!input,(c,d)=>c.pulseAlerts(d,input));
  measurementAlerts=(id:string,input?:import('./care-types').MeasurementAlertSettings)=>this.careRequest(id,!!input,(c,d)=>c.measurementAlerts(d,input));
  voices=(id:string)=>this.careRequest(id,false,(c,d)=>c.voices(d));
  voiceAudio=(id:string,messageId:number)=>this.careRequest(id,false,(c,d)=>c.voiceAudio(d,messageId));
  sendVoice=(id:string,audio:string,requestId:string)=>this.careRequest(id,true,(c,d)=>c.sendVoice(d,audio,requestId));
  deleteVoice=(id:string,messageId:number)=>this.careRequest(id,true,(c,d)=>c.deleteVoice(d,messageId));
  refresh = (automatic = false) => this.run((client, id) => this.readSnapshot(client, id), "local.dataRefreshed", false, automatic);
  readDevicePhoto=async()=>{
    this.checkExpiry();const id=this.selectedDeviceId,client=this.client;if(!id||!client||!this.state.signedIn)return null;
    if(this.photoCache?.deviceId===id&&this.photoCache.revision===this.state.snapshot?.device?.photoRevision)return this.photoCache;
    try{const result=await client.devicePhoto(id);if(client!==this.client||id!==this.selectedDeviceId||!this.state.signedIn)return null;this.photoCache=result;return result;}catch(error){if(client===this.client&&!client.signedIn)this.checkExpiry();throw error;}
  };
  saveDevicePhoto=(dataUri:string|null,revision:number,expectedId:string)=>this.run(async(client,id)=>{if(!id||id!==expectedId)throw new Error('missing_device');const result=await client.devicePhoto(id,{dataUri,revision});if(client===this.client&&id===this.selectedDeviceId)this.photoCache=result;return this.readSnapshot(client,id);},'local.dataRefreshed');
  readHealthSummary=async(kind:HealthMetric,period:ActivityPeriod,date:string):Promise<HealthSummary|null>=>{
    this.checkExpiry();const id=this.selectedDeviceId,client=this.client;if(!id||!client||!this.state.signedIn)return null;
    try{const result=await client.healthSummary(id,kind,period,date);return client===this.client&&id===this.selectedDeviceId&&this.state.signedIn?result:null;}catch(error){if(client===this.client&&!client.signedIn)this.checkExpiry();throw error;}
  };
  readArchive = async (input: ArchiveInput): Promise<ArchivePage | null> => {
    this.checkExpiry();
    const id = this.selectedDeviceId, client = this.client;
    if (!id || !client || !this.state.signedIn) return null;
    try {
      const result = await client.archivePage(id, input);
      return client === this.client && id === this.selectedDeviceId && this.state.signedIn ? result : null;
    } catch (error) {
      if (client !== this.client || id !== this.selectedDeviceId || !this.state.signedIn) return null;
      if (!client.signedIn) this.checkExpiry();
      throw error;
    }
  };
  pendingEventIds=async(input:{eventType?:string;startAt?:string;endAt?:string})=>{
    this.checkExpiry();const id=this.selectedDeviceId,client=this.client;
    if(!id||!client||!this.state.signedIn||this.state.snapshot?.device?.access?.canAcknowledge===false)return null;
    return client.pendingEventIds(id,input,()=>client===this.client&&id===this.selectedDeviceId&&this.state.signedIn);
  };
  acknowledgeSelection=async(deviceId:string,ids:number[])=>{
    this.checkExpiry();const client=this.client,current=()=>client===this.client&&deviceId===this.selectedDeviceId&&this.state.signedIn&&this.state.snapshot?.device?.access?.canAcknowledge!==false;
    if(!client||!current())return null;
    const succeeded:number[]=[],failed:number[]=[];this.publish({busy:true});
    try{for(let i=0;i<ids.length;i+=200){if(!current()){failed.push(...ids.slice(i));break;}const batch=ids.slice(i,i+200);try{const result=await client.acknowledgeBatch(deviceId,batch);succeeded.push(...result.succeeded);failed.push(...result.failed);}catch{failed.push(...ids.slice(i));break;}}
      if(current())try{const snapshot=await this.readSnapshot(client,deviceId);if(current())this.publish({snapshot});}catch{}
      return current()?{succeeded,failed}:null;
    }finally{if(client===this.client&&this.state.signedIn)this.publish({busy:false});}
  };
  acknowledgeArchived = (eventId: number, deviceId: string) => {
    if (deviceId !== this.selectedDeviceId||this.state.snapshot?.device?.access?.canAcknowledge===false) return Promise.resolve(false);
    return this.run(async (client, id) => {
      if (id !== deviceId || !Number.isSafeInteger(eventId) || eventId < 1) throw new LocalApiError("response");
      await client.acknowledge(deviceId, eventId);
      return this.readSnapshot(client, id);
    }, "local.acknowledgementSaved");
  };
  sendHardwareCommand = (type: HardwareCommandType, parameters: Record<string, unknown> = {}, confirmed = false, onAccepted?: (commandId: number) => void) => {
    if(this.state.snapshot?.device?.access?.canConfigure===false)return Promise.resolve(false);
    if (!confirmed || this.state.networkUnavailable || !this.state.snapshot?.device?.connected || !this.state.snapshot.hardware?.capabilities.commands.includes(type)) return Promise.resolve(false);
    return this.run(async (client, id) => {
      if (!id) throw new LocalApiError("deviceMissing");
      const accepted = await client.hardwareCommand(id, type, parameters);
      onAccepted?.(accepted.commandId);
      return this.readSnapshot(client, id);
    }, "hardware.accepted");
  };
  selectDevice = (id: string,preserveNavigation=false) => {
    if (this.state.busy) return Promise.resolve(false);
    if(!preserveNavigation)browserDeviceSelected(id,this.state.accountEmail);
    this.selectedDeviceId = id;
    const devices=this.state.snapshot?.devices??[];
    this.publish({snapshot:{devices,device:devices.find(d=>d.id===id)??null,position:null,events:[],loadedAt:new Date().toISOString()},dataUnavailable:true});
    this.routeRevision += 1;
    this.publish({ routeHistory: null, routeBusy: false, routeRangeKey: null, routeError: null, routeLoadedAt: null });
    return this.run((client) => this.readSnapshot(client, id), "local.dataRefreshed");
  };
  loadRoute = async (startAt: string, endAt: string, force = false,timeBasis:"receipt"|"measured"="receipt") => {
    this.checkExpiry();
    const id = this.selectedDeviceId, client = this.client;
    const key = `${id ?? ""}:${startAt}:${endAt}:${timeBasis}`;
    if (!id || !client || !this.state.signedIn) return false;
    if (!this.state.snapshot?.device?.capabilities?.routeHistory) return false;
    if (this.state.routeBusy && this.state.routeRangeKey === key) return false;
    const latest=this.state.snapshot?.position;
    const newerReport=Boolean(latest&&latest.id>(this.state.routeHistory?.snapshotId??0)&&latest.receivedAt>=startAt&&latest.receivedAt<endAt);
    if (!force && !newerReport && this.state.routeRangeKey === key && this.state.routeHistory && Date.now() - Date.parse(this.state.routeLoadedAt ?? "") < 30000) {
      return true;
    }
    const revision = ++this.routeRevision;
    this.publish({ routeBusy: true, routeRangeKey: key, routeHistory: null, routeError: null, routeLoadedAt: null });
    try {
      const routeHistory = await client.routeHistory(id, startAt, endAt, () => revision === this.routeRevision && this.state.signedIn && id === this.selectedDeviceId,timeBasis);
      if (revision !== this.routeRevision || id !== this.selectedDeviceId) return false;
      this.publish({ routeHistory, routeBusy: false, routeLoadedAt: new Date().toISOString() });
      // Gaps remain straight visual connectors; do not request invented road geometry.
      return true;
    } catch (error) {
      if (revision === this.routeRevision) {
        if (!client.signedIn) this.checkExpiry();
        else this.publish({ routeHistory: null, routeBusy: false, routeError: `local.error.${error instanceof LocalApiError ? error.code : "connection"}` });
      }
      return false;
    }
  };
  cancelRoute = () => {
    this.routeRevision += 1;
    if (this.state.routeBusy) this.publish({ routeBusy: false, routeRangeKey: null, routeError: null, routeLoadedAt: null });
  };
  loadActivity = async (period: ActivityPeriod, date: string, compare: boolean|string = false): Promise<boolean> => {
    this.checkExpiry();
    const id = this.selectedDeviceId, client = this.client;
    if (!id || !client || !this.state.signedIn || !this.state.snapshot?.device?.capabilities?.activitySummary) return false;
    const revision = ++this.activityRevision;
    this.publish({ activityBusy: true, activitySummary: null, previousActivitySummary: null, activityComparisonError: false, activityComparisonBusy: false, activityError: null });
    try {
      const currentRequest=()=>revision===this.activityRevision&&id===this.selectedDeviceId&&client===this.client;
      const fetchSummary=async(anchor:string)=>{const data=await client.activitySummary(id,period,anchor);if(period==='day'&&this.state.snapshot?.device?.capabilities?.routeHistory&&currentRequest()){const b=activityDayBounds(anchor);const route=await client.routeHistory(id,b.startAt,b.endAt,currentRequest);data.hours=hourlyActivity(route.points,anchor,data.profile);}return data;};
      const summary = await fetchSummary(date);
      if (revision !== this.activityRevision || id !== this.selectedDeviceId || client !== this.client) return false;
      const current = this.state.snapshot?.device?.activityProfile;
      if ((current?.revision ?? 0) > (summary.profile?.revision ?? 0)) return this.loadActivity(period, date, compare);
      this.publish({ activitySummary:summary, activityBusy:false, activityComparisonBusy:Boolean(compare) });
      let previous:ActivitySummary|null=null,comparisonError=false;
      if(compare){try{previous=await fetchSummary(typeof compare==='string'?compare:previousActivityAnchor(period,date));}catch{comparisonError=true;}}
      if(revision!==this.activityRevision||id!==this.selectedDeviceId||client!==this.client)return false;
      const latestProfile=this.state.snapshot?.device?.activityProfile?.revision??0;
      if(latestProfile>(summary.profile?.revision??0))return this.loadActivity(period,date,compare);
      if(previous&&(previous.profile?.revision??0)!==(summary.profile?.revision??0)){previous=null;comparisonError=true;}
      this.publish({ activitySummary: summary, previousActivitySummary:previous, activityComparisonError:comparisonError, activityComparisonBusy:false, activityBusy: false });
      return true;
    } catch (error) {
      if (revision === this.activityRevision) {
        if (!client.signedIn) this.checkExpiry();
        else this.publish({ activityBusy: false, activityComparisonBusy:false, activityError: `local.error.${error instanceof LocalApiError ? error.code : "connection"}` });
      }
      return false;
    }
  };
  cancelActivity = () => { this.activityRevision++; this.publish({ activityBusy: false, activityComparisonBusy:false }); };
  saveActivityProfile = async (strideCm: number, weightKg: number, expectedRevision: number) => {
    this.checkExpiry();
    const id = this.selectedDeviceId, client = this.client;
    if (!id || !client || !this.state.signedIn || this.state.profileBusy || !this.state.snapshot?.device?.capabilities?.activityProfile) return false;
    const revision = ++this.profileRevision;
    this.publish({ profileBusy: true, profileError: null });
    try {
      const profile = await client.saveActivityProfile(id, { strideCm, weightKg, revision: expectedRevision });
      if (revision !== this.profileRevision || client !== this.client || id !== this.selectedDeviceId || !this.state.snapshot?.device) return false;
      this.activityRevision++;
      this.publish({ profileBusy: false, activitySummary: null, previousActivitySummary: null, activityComparisonError: false, activityBusy: false,
        snapshot: { ...this.state.snapshot, device: { ...this.state.snapshot.device, activityProfile: profile } } });
      await this.cacheSnapshot(this.state.snapshot!);
      return revision===this.profileRevision&&client===this.client&&id===this.selectedDeviceId;
    } catch (error) {
      if (revision === this.profileRevision) {
        if (!client.signedIn) this.checkExpiry();
        else this.publish({ profileBusy: false, profileError: `local.error.${error instanceof LocalApiError ? error.code : "connection"}` });
      }
      return false;
    }
  };
  setTelemetryWindow = (minutes: TelemetryWindow) => {
    if (!validTelemetryWindow(minutes) || this.state.busy || this.state.telemetryMinutes === minutes) return Promise.resolve(false);
    this.publish({ telemetryMinutes: minutes });
    return this.refresh();
  };
  private clearPositionWait() {
    if (!this.positionWait) return;
    const pending = this.positionWait;
    this.positionWait = null;
    clearTimeout(pending.timer);
    pending.resolve();
  }
  cancelPositionRequest = () => {
    if (this.following !== "position") return;
    this.following = null;
    this.revision += 1;
    this.client?.cancelPending?.();
    this.clearPositionWait();
    this.publish({ busy: false, positionProgress: "cancelled", notice: null });
  };
  cancelFindRequest = () => {
    if (this.following !== "find") return;
    this.following = null;
    this.revision += 1;
    this.client?.cancelPending?.();
    this.clearPositionWait();
    this.publish({ busy: false, findProgress: "cancelled", notice: null });
  };
  confirmTrackerFound = () => {
    if (!["accepted", "sent", "replied"].includes(this.state.findProgress ?? "")) return false;
    if (this.following === "find") {
      this.following = null;
      this.revision += 1;
      this.client?.cancelPending?.();
      this.clearPositionWait();
    }
    this.publish({ busy: false, findProgress: "found", notice: null });
    return true;
  };
  cancelUploadRequest = () => {
    if (this.following !== "upload") return;
    this.following = null;
    this.revision += 1;
    this.client?.cancelPending?.();
    this.clearPositionWait();
    this.publish({ busy: false, uploadProgress: "cancelled", notice: null });
  };
  findTracker = async (confirmed: boolean) => {
    if(this.state.snapshot?.device?.access?.canConfigure===false)return false;
    this.checkExpiry();
    if (!confirmed || this.state.busy || !this.state.signedIn || !this.client) return false;
    const client = this.client, id = this.selectedDeviceId;
    if (!id || !this.state.snapshot?.device?.connected) return false;
    const revision = ++this.revision;
    this.following = "find";
    this.publish({ busy: true, notice: null, positionProgress: null, findProgress: "sending", uploadProgress: null });
    try {
      await followFindRequest(client, id, {
        isCurrent: () => revision === this.revision && this.state.signedIn,
        pause: milliseconds => new Promise<void>(resolve => {
          const timer = setTimeout(() => { this.positionWait = null; resolve(); }, milliseconds);
          this.positionWait = { timer, resolve };
        }),
        onUpdate: (snapshot, findProgress) => {
          if (revision === this.revision) this.publish({ snapshot, findProgress });
        },
        readSnapshot: deviceId => this.readSnapshot(client, deviceId),
      });
      if (revision !== this.revision) return false;
      this.following = null;
      this.publish({ busy: false });
      return true;
    } catch (error) {
      if (revision === this.revision) this.fail(error);
      return false;
    }
  };
  setUploadInterval = async (intervalSeconds: number, confirmed: boolean) => {
    if(this.state.snapshot?.device?.access?.canConfigure===false)return false;
    this.checkExpiry();
    if (!confirmed || this.state.busy || !this.state.signedIn || !this.client) return false;
    const client = this.client, id = this.selectedDeviceId;
    if (!id || !this.state.snapshot?.device?.connected) return false;
    const revision = ++this.revision;
    this.following = "upload";
    this.publish({ busy: true, notice: null, positionProgress: null, findProgress: null, uploadProgress: "sending" });
    try {
      await followUploadRequest(client, id, intervalSeconds, {
        isCurrent: () => revision === this.revision && this.state.signedIn,
        pause: milliseconds => new Promise<void>(resolve => {
          const timer = setTimeout(() => { this.positionWait = null; resolve(); }, milliseconds);
          this.positionWait = { timer, resolve };
        }),
        onUpdate: (snapshot, uploadProgress) => {
          if (revision === this.revision) this.publish({ snapshot, uploadProgress });
        },
        readSnapshot: deviceId => this.readSnapshot(client, deviceId),
      });
      if (revision !== this.revision) return false;
      this.following = null;
      this.publish({ busy: false });
      return true;
    } catch (error) {
      if (revision === this.revision) this.fail(error);
      return false;
    }
  };
  requestPosition = async () => {
    if(this.state.snapshot?.device?.access?.canConfigure===false)return false;
    this.checkExpiry();
    if (this.state.busy || !this.state.signedIn || !this.client) return false;
    const client = this.client, id = this.selectedDeviceId;
    if (!id || !this.state.snapshot?.device?.connected) return false;
    if(Date.now()<(this.positionAttempts.get(id)??0))return false;
    const positionCooldownUntil=Date.now()+POSITION_REQUEST_LOCK_MS;
    this.positionAttempts.set(id,positionCooldownUntil);
    const revision = ++this.revision;
    this.following = "position";
    this.publish({ busy: true, notice: null, positionProgress: "sending", findProgress: null, uploadProgress: null, positionCooldownUntil, positionRequestDeviceId:id });
    try {
      await followPositionRequest(client, id, {
        isCurrent: () => revision === this.revision && this.state.signedIn,
        onRequested: observation => { if(revision===this.revision)this.positionObservation={deviceId:id,...observation}; },
        pause: milliseconds => new Promise<void>(resolve => {
          const timer = setTimeout(() => { this.positionWait = null; resolve(); }, milliseconds);
          this.positionWait = { timer, resolve };
        }),
        onUpdate: (snapshot, positionProgress) => {
          if (revision === this.revision) this.publish({ snapshot, positionProgress });
        },
        readSnapshot: deviceId => this.readSnapshot(client, deviceId),
      });
      if (revision !== this.revision) return false;
      this.following = null;
      this.publish({ busy: false });
      return true;
    } catch (error) {
      if (revision === this.revision) {
        if(!client.signedIn)this.fail(error);
        else {this.following=null;this.clearPositionWait();this.publish({busy:false,autoRefreshing:false,positionProgress:"failed",networkUnavailable:error instanceof LocalApiError&&["connection","server","response"].includes(error.code),notice:null});}
      }
      return false;
    }
  };
  acknowledge = (eventId: number) => this.state.snapshot?.device?.access?.canAcknowledge===false?Promise.resolve(false):this.run(async (client, id) => {
    const event = this.state.snapshot?.events.find((candidate) => candidate.id === eventId);
    if (!id || !event) throw new LocalApiError("response");
    await client.acknowledge(id, eventId);
    return this.readSnapshot(client, id);
  }, "local.acknowledgementSaved");
  updateDeviceName = (displayName: string) => this.state.snapshot?.device?.access?.canConfigure===false?Promise.resolve(false):this.run(async (client, id) => {
    if (!id) throw new LocalApiError("deviceMissing");
    await client.updateDeviceName(id, displayName);
    return this.readSnapshot(client, id);
  }, "deviceName.saved");
  updateDeviceContact = async (phoneNumber: string | null, contactRevision: number) => {
    if(this.state.snapshot?.device?.access?.canConfigure===false)return false;
    this.checkExpiry();
    if (this.state.busy || !this.client || !this.state.signedIn || !this.selectedDeviceId) return false;
    const client = this.client, id = this.selectedDeviceId, revision = ++this.revision;
    this.publish({ busy: true, notice: null });
    try {
      const contact = await client.updateDeviceContact(id, phoneNumber, contactRevision);
      if (revision !== this.revision) return false;
      const snapshot = this.state.snapshot;
      if (!snapshot?.device || snapshot.device.id !== id) return false;
      this.publish({ snapshot: { ...snapshot, device: { ...snapshot.device, contact } }, busy: false, notice: { key: "contact.saved", error: false } });
      return true;
    } catch (error) {
      if (revision !== this.revision) return false;
      if (!client.signedIn) this.fail(error);
      else {
        // Keep the number draft mounted on connection/conflict errors. A failed
        // HTTP response is not proof that a save failed; never retry by itself.
        if (error instanceof LocalApiError && error.code === "contactChanged") {
          try {
            const snapshot = await this.readSnapshot(client, id);
            if (revision !== this.revision) return false;
            this.publish({ snapshot });
          } catch { /* Retain the last known value and explicit error. */ }
        }
        if (revision === this.revision) this.publish({ busy: false, notice: { key: `local.error.${error instanceof LocalApiError ? error.code : "connection"}`, error: true } });
      }
      return false;
    }
  };
  async changePassword(currentPassword: string, newPassword: string) {
    this.checkExpiry();
    if (!this.client || !this.state.signedIn || this.state.busy || this.state.passwordBusy) return false;
    const active = this.client;
    this.revision++; active.cancelPending();
    this.publish({ busy: true, passwordBusy: true, passwordError: null, notice: null });
    try {
      await active.changePassword(currentPassword, newPassword);
      if (active !== this.client || !this.state.signedIn) return false;
      await this.writeVault(null);
      await this.clearCachedSnapshot();
      this.dispose();
      this.publish({ notice: { key: "password.changed", error: false } });
      return true;
    } catch (error) {
      if (active !== this.client || !this.state.signedIn) return false;
      const code = error instanceof LocalApiError ? error.code : "passwordOutcomeUnknown";
      if (!active.signedIn) {
        await this.writeVault(null);
      this.dispose(); this.publish({ notice: { key: `local.error.${code}`, error: true } });
      } else this.publish({ busy: false, passwordBusy: false, passwordError: `local.error.${code}` });
      return false;
    }
  }
  async changeNamedZone(input:Parameters<LocalApiClient['changeNamedZone']>[1],remove=false){
    if(this.state.snapshot?.device?.access?.canConfigure===false)return false;
    const active=this.client,id=this.selectedDeviceId;
    if(!active||!id||!this.state.signedIn||this.state.busy||this.state.networkUnavailable||!this.state.snapshot?.device?.capabilities?.gpsZones)return false;
    this.publish({busy:true,notice:null});
    try{const zones=await active.changeNamedZone(id,input,remove);if(active!==this.client||id!==this.selectedDeviceId||!this.state.signedIn)return false;
      const snapshot=this.state.snapshot;if(snapshot?.device)this.publish({busy:false,snapshot:{...snapshot,device:{...snapshot.device,gpsZones:zones}},notice:{key:remove?'product.zoneRemoved':'product.zoneSaved',error:false}});
      await this.refresh(true);return true;
    }catch(error){if(active!==this.client||id!==this.selectedDeviceId||!this.state.signedIn)return false;
      this.publish({busy:false});await this.refresh(true);
      if(active===this.client&&id===this.selectedDeviceId)this.publish({notice:{key:remove?'product.zoneRemoveFailed':'product.zoneSaveFailed',error:true}});
      return false;
    }
  }
  async saveGpsZone(zone:Omit<import("./local-api").GpsZone,"revision"|"updatedAt">,revision:number) {
    if(this.state.snapshot?.device?.access?.canConfigure===false)return false;
    if(!this.client||!this.state.signedIn||this.state.busy||!this.selectedDeviceId)return false;
    const active=this.client,id=this.selectedDeviceId;this.publish({busy:true,notice:null});
    try{const saved=await active.saveGpsZone(id,zone,revision);if(active!==this.client||id!==this.selectedDeviceId||!this.state.signedIn)return false;const snapshot=this.state.snapshot;if(snapshot?.device)this.publish({busy:false,snapshot:{...snapshot,device:{...snapshot.device,gpsZone:saved,gpsZoneRevision:saved.revision}},notice:{key:"product.zoneSaved",error:false}});return true;}
    catch(error){
      if(active===this.client&&id===this.selectedDeviceId&&this.state.signedIn){
        this.publish({busy:false});await this.refresh();
        if(active===this.client&&id===this.selectedDeviceId&&this.state.signedIn)this.publish({notice:{key:"product.zoneSaveFailed",error:true}});
      }
      return false;
    }
  }
  async deleteGpsZone(revision:number,confirmed=false) {
    if(this.state.snapshot?.device?.access?.canConfigure===false)return false;
    if(!confirmed||!this.client||!this.state.signedIn||this.state.busy||!this.selectedDeviceId||!this.state.snapshot?.device?.capabilities?.gpsZoneDelete)return false;
    const active=this.client,id=this.selectedDeviceId;this.publish({busy:true,notice:null});
    try{
      const savedRevision=await active.deleteGpsZone(id,revision);
      if(active!==this.client||id!==this.selectedDeviceId||!this.state.signedIn)return false;
      const snapshot=this.state.snapshot;
      if(snapshot?.device)this.publish({busy:false,snapshot:{...snapshot,device:{...snapshot.device,gpsZone:null,gpsZoneRevision:savedRevision}},notice:{key:"product.zoneRemoved",error:false}});
      return true;
    }catch(error){
      if(active===this.client&&id===this.selectedDeviceId&&this.state.signedIn){
        this.publish({busy:false});await this.refresh();
        if(active===this.client&&id===this.selectedDeviceId&&this.state.signedIn)this.publish({notice:{key:"product.zoneRemoveFailed",error:true}});
      }
      return false;
    }
  }
  async saveDashboard(cards:string[],layout?:DashboardLayout) {
    if(!this.client||!this.state.signedIn||this.state.dashboardBusy||!validDashboard(cards))return false;
    const active=this.client;this.publish({dashboardBusy:true,dashboardError:null,notice:null});
    try{const saved=await active.saveDashboard(cards,layout);if(active!==this.client||!this.state.signedIn)return false;this.publish({dashboard:saved,dashboardLayout:active.dashboardLayout,dashboardBusy:false});return true;}
    catch(error){if(active===this.client&&this.state.signedIn)this.publish({dashboardBusy:false,dashboardError:error instanceof LocalApiError?error.code:'connection',notice:{key:`local.error.${error instanceof LocalApiError?error.code:"connection"}`,error:true}});return false;}
  }
  async saveLayout(layout: "simple" | "modern" | "technical" | "modern2" | "accessible") {
    if (!this.client || !this.state.signedIn || this.state.layoutBusy) return false;
    const active = this.client;
    this.publish({ layoutBusy: true });
    try {
      await active.saveLayout(layout);
      if (active !== this.client || !this.state.signedIn) return false;
      this.publish({ layoutPreference: layout, layoutBusy: false });
      return true;
    } catch {
      if (active === this.client && this.state.signedIn) this.publish({ layoutBusy: false, notice: { key: "layout.unsaved", error: true } });
      return false;
    }
  }
  private appearanceWrites:Promise<unknown>=Promise.resolve();
  saveAppearance(patch:Partial<Appearance>):Promise<boolean> {
    const active=this.client,revision=this.revision;
    if(!active||!this.state.signedIn)return Promise.resolve(false);
    // Serialize rapid selections and discard completions from a previous login.
    const run=this.appearanceWrites.catch(()=>{}).then(async()=>{
      if(active!==this.client||revision!==this.revision||!this.state.signedIn)return false;
      this.publish({layoutBusy:true});
      try {
        await active.saveAppearance(patch);
        if(active!==this.client||revision!==this.revision||!this.state.signedIn)return false;
        this.publish({appearancePreference:active.appearance,layoutBusy:false});return true;
      }catch {
        if(active===this.client&&revision===this.revision&&this.state.signedIn)this.publish({layoutBusy:false,notice:{key:'layout.unsaved',error:true}});
        return false;
      }
    });
    this.appearanceWrites=run;return run;
  }
  private async notificationAction<T>(action:(client:LocalApiClient)=>Promise<T>):Promise<T>{
    const client=this.client;if(!client||!this.state.signedIn)throw new LocalApiError("session");
    const result=await action(client);if(client!==this.client||!this.state.signedIn)throw new LocalApiError("cancelled");return result;
  }
  accountAction=(action:Parameters<LocalApiClient['accountAction']>[0],body:Record<string,unknown>,address=this.state.address,mode=this.state.mode)=>this.makeClient(address,mode).accountAction(action,body);
  accountExport=()=>this.notificationAction(client=>client.accountExport());
  accountDeletion=()=>this.notificationAction(client=>client.accountDeletion());
  requestAccountDeletion=(password:string)=>this.notificationAction(client=>client.requestAccountDeletion(password));
  customerAccount=()=>this.notificationAction(client=>client.customerAccount());
  accountRemovalDevices=()=>this.notificationAction(client=>client.accountRemovalDevices());
  refreshDeviceSelection=async()=>{const before=this.selectedDeviceId;const ok=await this.run(async(client,id)=>{try{return await this.readSnapshot(client,id);}catch(e){if(!(e instanceof LocalApiError)||e.code!=='deviceMissing')throw e;return this.readSnapshot(client);}},'local.dataRefreshed');if(ok&&before!==this.selectedDeviceId)browserDeviceSelected(this.selectedDeviceId,this.state.accountEmail);return ok;};
  customerMutation=(action:Parameters<LocalApiClient['customerMutation']>[0],body:Record<string,unknown>,method='POST')=>this.notificationAction(client=>client.customerMutation(action,body,method));
  customerMembers=(id:string)=>this.notificationAction(client=>client.customerMembers(id));
  assignedFleets=()=>this.notificationAction(client=>client.assignedFleets());
  fleetAccess=()=>this.notificationAction(client=>client.fleetAccess());
  createFleet=(body:Record<string,unknown>)=>this.notificationAction(client=>client.createFleet(body));
  assignedFleet=(id:string)=>this.notificationAction(client=>client.assignedFleet(id));
  fleetAction=(id:string,resource:string,method='GET',body?:Record<string,unknown>)=>this.notificationAction(client=>client.fleetAction(id,resource,method,body));
  fleetEvents=(id:string,startAt:string,endAt:string,isCurrent=()=>true)=>this.notificationAction(client=>client.fleetEvents(id,startAt,endAt,isCurrent));
  fleetRouteHistory=(id:string,deviceId:string,startAt:string,endAt:string,isCurrent=()=>true)=>this.notificationAction(client=>client.fleetRouteHistory(id,deviceId,startAt,endAt,isCurrent));
  fleet=()=>this.notificationAction(client=>client.fleet());
  devicePage=(q='',cursor:string|null=null,selectedId?:string)=>this.notificationAction(client=>client.devicePage(q,cursor,selectedId));
  restartGps=(id:string)=>this.notificationAction(client=>client.restartGps(id));
  gpsInterval=(id:string)=>this.notificationAction(client=>client.gpsInterval(id));
  gpsSetting=(id:string,action:import('./local-api').GpsSettingAction)=>this.notificationAction(client=>client.gpsSetting(id,action));
  setGpsSetting=(id:string,action:import('./local-api').GpsSettingAction,value:number)=>this.notificationAction(client=>client.setGpsSetting(id,action,value));
  alertEmail=(id:string)=>this.notificationAction(client=>client.alertEmail(id));
  batteryAlerts=(id:string)=>this.notificationAction(client=>client.batteryAlerts(id));
  setBatteryAlerts=(id:string,input:BatteryAlertInput)=>this.notificationAction(client=>client.setBatteryAlerts(id,input));
  setAlertEmail=(id:string,enabled:boolean,locale:string)=>this.notificationAction(client=>client.setAlertEmail(id,enabled,locale));
  gpsMode=(id:string)=>this.notificationAction(client=>client.gpsMode(id));
  setGpsMode=(id:string,value:number)=>this.notificationAction(client=>client.setGpsMode(id,value));
  gpsParameters=(id:string,query:string="parameters")=>this.notificationAction(client=>client.gpsParameters(id,query));
  queryGpsParameters=(id:string,query:string="parameters")=>this.notificationAction(client=>client.queryGpsParameters(id,query));
  setGpsInterval=(id:string,value:number,accOff?:number)=>this.notificationAction(client=>client.setGpsInterval(id,value,accOff));
  requestGpsPosition=(id:string)=>this.notificationAction(client=>client.requestGpsPosition(id));
  speedAlert=(id:string,input?:import('./customer-types').SpeedRule)=>this.notificationAction(client=>client.speedAlert(id,input));
  notificationSettings=()=>this.notificationAction(client=>client.notificationSettings());
  alertRecipients=(deviceId:string)=>this.notificationAction(client=>client.alertRecipients(deviceId));
  registerNotifications=(input:Parameters<LocalApiClient["registerNotifications"]>[0])=>this.notificationAction(client=>client.registerNotifications(input));
  restoreNotifications=(input:Parameters<LocalApiClient["restoreNotifications"]>[0])=>this.notificationAction(client=>client.restoreNotifications(input));
  disableNotifications=()=>this.notificationAction(client=>client.disableNotifications());
  reportNotification=(id:string,kind:"received"|"opened")=>this.notificationAction(client=>client.reportNotification(id,kind));
  notificationEvent=(deviceId:string,eventId:number)=>this.notificationAction(client=>client.notificationEvent(deviceId,eventId));
  async logout() {
    const revision=++this.revision;this.routeRevision++;
    const active=this.client,remembered=active?.rememberedLogin;
    this.client=null;this.selectedDeviceId=undefined;this.cancelExpiry();
    this.publish({signedIn:false,busy:true,accountEmail:null,snapshot:null,notice:null,positionProgress:null,findProgress:null,uploadProgress:null,routeHistory:null,routeBusy:false,routeRangeKey:null,routeError:null,routeLoadedAt:null,autoRefreshing:false});
    active?.cancelPending();
    await this.clearCachedSnapshot();
    let localFailed=false,remoteFailed=false;
    try{
      await this.vaultWrites.catch(()=>{});
      if(remembered&&this.vault?.queueRevocation){await this.vault.queueRevocation(remembered);this.needsRevocation=true;}
      else await this.writeVault(null);
    }catch{localFailed=true;}
    // A storage failure must not prevent revoking the remote session.
    try{await active?.logout();}catch{remoteFailed=true;}
    if(!remoteFailed){
      try{if(remembered&&this.vault?.removeRevocation)await this.vault.removeRevocation(remembered.refreshToken);if(localFailed)await this.writeVault(null);localFailed=false;}catch{localFailed=true;}
    }
    if(revision===this.revision)this.publish({busy:false,notice:localFailed?{key:"local.error.logoutStorage",error:true}:remoteFailed?{key:"local.logoutLocalOnly",error:true}:null});
    if(!remoteFailed)void this.drainRevocations();else this.scheduleRefresh();
  }
  dispose() {
    this.revision += 1; this.routeRevision += 1; this.cancelExpiry(); this.client?.clear(); this.client = null; this.selectedDeviceId = undefined;
    this.publish({ signedIn: false, busy: false, accountEmail: null, snapshot: null, notice: null, positionProgress: null, findProgress: null, uploadProgress: null, routeHistory: null, routeBusy: false, routeRangeKey: null, routeError: null, routeLoadedAt: null, autoRefreshing: false });
  }
}
